import type { AvailableCoupon } from '@/features/campaigns/api/couponApi';

/** What a coupon does to an order of `base` rupees (the amount before GST), or why it cannot apply yet. */
export type Offer = {
  coupon: AvailableCoupon;
  code: string;
  /** Rupees saved on `base`; 0 when the order is below the coupon's minimum. */
  discount: number;
  /** For a locked offer: how much more the order needs to reach the minimum. */
  unlockNeeds: number;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/** The coupon's discount on an order, using the same rules as the server (percent or flat, capped). */
export function couponDiscount(coupon: AvailableCoupon, base: number): number {
  const minSpend = Number(coupon.minSpend ?? 0);
  if (minSpend > 0 && base < minSpend) return 0;

  const value = Number(coupon.discountValue ?? 0);
  const cap = Number((coupon as { maxSpend?: number }).maxSpend ?? 0);
  let discount = coupon.discountType === 'PERCENTAGE' ? (base * value) / 100 : Math.min(value, base);
  if (cap > 0 && discount > cap) discount = cap;
  return round2(Math.max(0, discount));
}

/**
 * Every offer for the current order: usable ones first, best saving on top, then the ones that
 * unlock with a slightly bigger order (nearest first).
 */
export function rankOffers(coupons: AvailableCoupon[], base: number): Offer[] {
  const offers = coupons
    .filter((coupon) => coupon.couponCode)
    .map((coupon): Offer => {
      const minSpend = Number(coupon.minSpend ?? 0);
      return {
        coupon,
        code: String(coupon.couponCode).toUpperCase(),
        discount: couponDiscount(coupon, base),
        unlockNeeds: minSpend > base ? round2(minSpend - base) : 0,
      };
    });

  const usable = offers.filter((o) => o.discount > 0).sort((a, b) => b.discount - a.discount);
  const locked = offers.filter((o) => o.discount <= 0 && o.unlockNeeds > 0).sort((a, b) => a.unlockNeeds - b.unlockNeeds);
  return [...usable, ...locked];
}

/** A short, plain line for an offer: "Flat ₹100 off" or "10% off up to ₹250". */
export function offerHeadline(coupon: AvailableCoupon): string {
  const value = Number(coupon.discountValue ?? 0);
  const cap = Number((coupon as { maxSpend?: number }).maxSpend ?? 0);
  if (coupon.discountType === 'PERCENTAGE') {
    return `${value}% off${cap > 0 ? ` up to ₹${cap.toLocaleString('en-IN')}` : ''}`;
  }
  return `Flat ₹${value.toLocaleString('en-IN')} off`;
}
